import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import moment from "moment-timezone";
import {shippingMethodDisplayName} from "../../utils/shippingMethodPresentation.js";
import {reportedCentreAverageOrderValue} from "../../utils/reportingMetricPresentation.js";
import ReportingInfoTooltip from "../../components/ReportingInfoTooltip.jsx";
import {dailyCoreMetrics, dailyEmptyStateAlert, isDailyReportGenuinelyEmpty, shouldShowDailyAvailabilityNotice} from "../../utils/reportingDailyPresentation.js";
import {snapshotHasPublishedDays, snapshotRowsForPublishedDays} from "../../utils/reportingSnapshotAvailability.js";
import ReportingSnapshotStatus from "./ReportingSnapshotStatus.jsx";
import CentreSpendAttributionNotice from "./CentreSpendAttributionNotice.jsx";
import {
    ChannelMixColors,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatCurrencyItemTooltip,
    KpiCard,
    moneyAmount,
    moneyToMajor,
    OrderStatusSummary,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    snapshotCompletenessAlert,
} from "./ReportingLab.jsx";

const DayMode = {
    Today: "today",
    Yesterday: "yesterday",
};

function rowName(row, fallback = "-") {
    return row?.name || row?.domain || row?.domainName || row?.hostName || row?.countryName || row?.coupon || row?.productName || row?.centreName || fallback;
}

function firstMoney(...values) {
    return values.find((value) => value && typeof value === "object" && Object.prototype.hasOwnProperty.call(value, "amount")) ?? null;
}

function readMoneyAmount(value) {
    if (value && typeof value === "object" && Object.prototype.hasOwnProperty.call(value, "amount")) {
        return moneyAmount(value);
    }

    return Number(value ?? 0);
}

function aggregateAmount(rows, nameGetter, valueGetter) {
    const map = new Map();

    rows.forEach((row) => {
        const name = nameGetter(row) || "-";
        map.set(name, (map.get(name) ?? 0) + Number(valueGetter(row) ?? 0));
    });

    return [...map.entries()]
        .map(([name, value]) => ({name, value}))
        .filter((row) => row.value > 0)
        .sort((a, b) => b.value - a.value);
}

export function metricChange(current, previous, percentagePoints = false) {
    if (current === null || current === undefined || previous === null || previous === undefined
        || !Number.isFinite(Number(current)) || !Number.isFinite(Number(previous))) {
        return null;
    }

    if (percentagePoints) return Number(current) - Number(previous);
    if (Number(previous) === 0) return null;

    return ((Number(current) - Number(previous)) / Math.abs(Number(previous))) * 100;
}

export function dailyOrderComparisonValues(currentMetrics, previousDayValues, previousWeekValues) {
    return {
        current: currentMetrics?.ordersTotal ?? null,
        previousDay: previousDayValues?.ordersTotal ?? null,
        previousWeek: previousWeekValues?.ordersTotal ?? null,
    };
}

export function comparisonTone(change, lowerIsBetter = false) {
    if (change === null || !Number.isFinite(change) || change === 0) {
        return "neutral";
    }

    return (lowerIsBetter ? change < 0 : change > 0) ? "positive" : "negative";
}

export function comparisonBadge(previousDay, current, t, lowerIsBetter = false, percentagePoints = false) {
    const change = metricChange(current, previousDay, percentagePoints);
    const displayedChange = change === null ? null : Math.round(change * 10) / 10;
    const value = displayedChange === null
        ? t("reporting_lab.daily.not_comparable", {defaultValue: "nelze porovnat"})
        : `${displayedChange > 0 ? "+" : ""}${formatChartNumber(displayedChange, {maximumFractionDigits: 1})} ${percentagePoints ? t("reporting_lab.pno.percentage_point_unit", {defaultValue: "p. b."}) : "%"}`;
    const tone = comparisonTone(displayedChange, lowerIsBetter);
    const toneClass = tone === "neutral"
        ? "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
        : tone === "positive"
            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300"
            : "bg-rose-100 text-datixo-negative dark:bg-rose-950/60 text-datixo-negative";

    const comparisonLabel = t("reporting_lab.daily.vs_previous_day", {defaultValue: "oproti předchozímu dni"});
    return <span
        className={`whitespace-nowrap rounded-md px-2 py-1 text-xs font-semibold ${toneClass}`}
        title={`${comparisonLabel}: ${value}`}
        aria-label={`${comparisonLabel}: ${value}`}
    >
        {value}
    </span>;
}

export function comparisonWeekMeta(current, previousWeek, formatter, t, lowerIsBetter = false, percentagePoints = false) {
    const change = metricChange(current, previousWeek, percentagePoints);
    const label = t("reporting_lab.daily.vs_previous_week", {defaultValue: "vs stejný den minulý týden"});

    if (change === null) {
        return <span className="mt-2 block border-t border-slate-200 pt-2 text-xs dark:border-slate-700">{label} {t("reporting_lab.daily.not_comparable", {defaultValue: "nelze porovnat"})}</span>;
    }

    const displayedChange = Math.round(change * 10) / 10;
    const changeSign = displayedChange > 0 ? "+" : "";
    const value = `${formatter(previousWeek)} (${changeSign}${formatChartNumber(displayedChange, {maximumFractionDigits: 1})} ${percentagePoints ? t("reporting_lab.pno.percentage_point_unit", {defaultValue: "p. b."}) : "%"})`;
    const tone = comparisonTone(displayedChange, lowerIsBetter);
    const toneClass = tone === "neutral"
        ? "text-slate-900 dark:text-slate-100"
        : tone === "positive"
            ? "text-emerald-600 dark:text-emerald-400"
            : "text-datixo-negative";

    return <span className="mt-2 block border-t border-slate-200 pt-2 text-xs dark:border-slate-700">{label} <strong className={`whitespace-nowrap font-semibold ${toneClass}`}>{value}</strong></span>;
}

function valueWithComparison(value, current, previousDay, t, lowerIsBetter = false, percentagePoints = false) {
    return <span className="flex flex-wrap items-center gap-2">
        <span>{value}</span>
        {comparisonBadge(previousDay, current, t, lowerIsBetter, percentagePoints)}
    </span>;
}

function moneyBarOption(rows, currency, title, chartTextStyle) {
    const chartRows = rows.slice(0, 10);

    return {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 16, bottom: 24, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...chartRows].reverse().map((row) => row.name),
            axisLabel: {fontSize: 12, width: 230, overflow: "truncate"},
        },
        series: [
            {
                name: title,
                type: "bar",
                data: [...chartRows].reverse().map((row, index) => ({
                    value: row.value,
                    itemStyle: {color: ChannelMixColors[(chartRows.length - 1 - index) % ChannelMixColors.length]},
                })),
                barMaxWidth: 24,
            },
        ],
    };
}

function DayModeToggle({value, onChange}) {
    const {t} = useTranslation();
    const options = [
        {
            value: DayMode.Today,
            label: t("reporting_lab.daily.today", {defaultValue: "Dnes"}),
        },
        {
            value: DayMode.Yesterday,
            label: t("reporting_lab.daily.yesterday", {defaultValue: "Včera"}),
        },
    ];

    return <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        {options.map((option) => (
            <button
                key={option.value}
                type="button"
                onClick={() => onChange(option.value)}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${
                    value === option.value
                        ? "bg-violet-600 text-white shadow-sm"
                        : "text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
            >
                {option.label}
            </button>
        ))}
    </div>;
}

export default function ReportingDailyLab({project}) {
    const {t} = useTranslation();
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const [data, setData] = useState(null);
    const [dataRequestKey, setDataRequestKey] = useState(null);
    const [snapshots, setSnapshots] = useState([]);
    const [loading, setLoading] = useState({
        core: true,
        products: true,
        coupons: true,
        payments: true,
        shippings: true,
        countries: true,
    });
    const {et, retentionExpired} = useReportingError(project.id, setLoading);
    const [dayMode, setDayMode] = useState(DayMode.Today);

    const day = useMemo(() => {
        const date = moment();

        if (dayMode === DayMode.Yesterday) {
            date.subtract(1, "day");
        }

        return date.format("YYYY-MM-DD");
    }, [dayMode]);
    const requestKey = `${project?.id ?? ""}:${day}`;

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setData(null);
        setDataRequestKey(null);
        setSnapshots([]);
        setLoading({core: true, products: true, coupons: true, payments: true, shippings: true, countries: true});

        const api = new ErpCalculationApi(getAuthorityConfiguration());
        const dayInput = {day};
        const finishPanel = (panel) => {
            if (active) {
                setLoading((current) => ({...current, [panel]: false}));
            }
        };
        const handleError = (error) => {
            if (active) {
                et(error).then((message) => { if (message) toast.error(message); });
            }
        };
        const loadPanel = (panel, promise) => promise
            .then((value) => {
                if (active) {
                    setData((current) => ({...(current ?? {}), [panel]: value}));
                    setDataRequestKey(requestKey);
                    finishPanel(panel);
                }
            })
            .catch(handleError);

        api.reportingDaily({projectId: project.id, dayInput})
            .then((result) => {
                if (!active) {
                    return;
                }

                setData((current) => ({
                    ...(current ?? {}),
                    orders: result.current?.orders,
                    channels: result.current?.channels,
                    centreSpendAttribution: result.current?.centreSpendAttribution ?? null,
                    domains: result.domains,
                    previousDayOrders: result.previousDay?.orders,
                    previousDayChannels: result.previousDay?.channels,
                    previousWeekOrders: result.previousWeek?.orders,
                    previousWeekChannels: result.previousWeek?.channels,
                }));
                setSnapshots([result.currentSnapshot, result.previousDaySnapshot, result.previousWeekSnapshot]);
                setDataRequestKey(requestKey);
                finishPanel("core");
            })
            .catch(handleError);

        loadPanel("products", api.dailyProductPerformance({
            projectId: project.id,
            productPerformanceDayInput: {day, limit: 10, page: 1},
        }));
        loadPanel("coupons", api.dailyCouponPerformance({projectId: project.id, dayInput}));
        loadPanel("payments", api.dailyPaymentPerformance({projectId: project.id, dayInput}));
        loadPanel("shippings", api.dailyShippingPerformance({projectId: project.id, dayInput}));
        loadPanel("countries", api.dailyCountryPerformance({projectId: project.id, dayInput}));

        return () => {
            active = false;
        };
    }, [project?.id, day]);

    const hasCurrentRequestData = dataRequestKey === requestKey;
    const currentData = hasCurrentRequestData ? data : null;
    const currentSnapshots = hasCurrentRequestData ? snapshots : [];
    const currentLoading = hasCurrentRequestData ? loading : {core: true, products: true, coupons: true, payments: true, shippings: true, countries: true};
    const currentMetrics = dailyCoreMetrics({orders: currentData?.orders, channels: currentData?.channels}, currentSnapshots[0]);
    const previousDayValues = dailyCoreMetrics({orders: currentData?.previousDayOrders, channels: currentData?.previousDayChannels}, currentSnapshots[1]);
    const previousWeekValues = dailyCoreMetrics({orders: currentData?.previousWeekOrders, channels: currentData?.previousWeekChannels}, currentSnapshots[2]);
    const isEmptySelectedDay = isDailyReportGenuinelyEmpty({
        data: currentData,
        loading: currentLoading,
        selectedRequestKey: requestKey,
        responseRequestKey: dataRequestKey,
        snapshot: currentSnapshots[0],
        selectedDay: day,
    });
    const showAvailabilityNotice = shouldShowDailyAvailabilityNotice({
        loading: currentLoading,
        availability: currentMetrics.availability,
        isEmpty: isEmptySelectedDay,
        data: currentData,
        snapshot: currentSnapshots[0],
        selectedDay: day,
    });
    const currentCoreAvailable = currentMetrics.availability === "available";
    const dailyCentres = currentCoreAvailable ? (currentData?.orders?.centres ?? []) : [];
    const centres = dailyCentres;
    const channels = currentCoreAvailable ? (currentData?.channels?.topChannels ?? []) : [];
    const productPublication = currentData?.products?.productPublication;
    const productsByTurnover = snapshotRowsForPublishedDays(currentData?.products?.topProductsByTurnoverGross ?? [], productPublication);
    const coupons = currentData?.coupons ?? [];
    const payments = currentData?.payments?.payments ?? [];
    const shippings = currentData?.shippings?.shippings ?? [];
    const countries = currentData?.countries?.topCountries ?? [];
    const domains = currentCoreAvailable
        ? (Array.isArray(currentData?.domains?.dataByDomain)
            ? currentData.domains.dataByDomain
            : (Array.isArray(currentData?.domains) ? currentData.domains : Object.values(currentData?.domains?.dataByDomain ?? {})))
        : [];

    const currency = centres.find((row) => row.turnover?.currency)?.turnover?.currency
        ?? channels.find((row) => row.turnoverGross?.currency)?.turnoverGross?.currency
        ?? productsByTurnover.find((row) => row.turnover?.currency)?.turnover?.currency
        ?? domains.map((row) => firstMoney(row?.turnover, row?.turnoverGross, row?.revenue, row?.spend, row?.value)).find(Boolean)?.currency
        ?? projectCurrency(project);

    const turnover = currentMetrics.turnover;
    const marketingSpend = currentMetrics.marketingSpend;
    const pno = currentMetrics.pno;
    const ordersBasis = currentMetrics.ordersBasis;
    const ordersComparison = dailyOrderComparisonValues(currentMetrics, previousDayValues, previousWeekValues);
    const averageOrderValue = currentMetrics.averageOrderValue;
    const channelRows = channels
        .map((channel) => ({
            name: channel.name ?? "-",
            value: moneyToMajor(channel.turnoverGross),
        }))
        .filter((row) => row.value > 0)
        .sort((a, b) => b.value - a.value);
    const productRows = productsByTurnover
        .map((product) => ({name: product.productName ?? "-", value: moneyToMajor(product.turnover)}))
        .filter((row) => row.value > 0)
        .sort((a, b) => b.value - a.value);
    const paymentRows = aggregateAmount(payments, rowName, (row) => row.amount);
    const shippingRows = aggregateAmount(shippings, (row) => shippingMethodDisplayName(row, t), (row) => row.amount);
    const countryRows = aggregateAmount(countries, rowName, (row) => row.amount);
    const domainRows = domains
        .map((domain) => ({
            name: domain?.projectDomain?.hostName ?? rowName(domain),
            turnoverAmount: Object.values(domain?.turnoverGrossGranular ?? {}).reduce((sum, money) => sum + readMoneyAmount(money), 0),
        }))
        .filter((domain) => domain.name !== "-" || domain.turnoverAmount > 0)
        .sort((a, b) => b.turnoverAmount - a.turnoverAmount);
    const couponRows = coupons
        .map((coupon) => ({name: coupon.coupon ?? "-", amount: coupon.amount ?? 0, turnover: moneyAmount(coupon.spends)}))
        .sort((a, b) => b.amount - a.amount);

    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const channelOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "item",
            formatter: (params) => formatCurrencyItemTooltip(params, currency),
        },
        legend: {bottom: 0, left: "center", width: "92%", ...chartLegendStyle},
        series: [
            {
                type: "pie",
                radius: ["44%", "66%"],
                center: ["50%", "44%"],
                avoidLabelOverlap: true,
                label: {
                    show: true,
                    formatter: (params) => `${params.name}: ${formatChartNumber(params.percent)} %`,
                    ...chartTextStyle,
                },
                labelLine: {show: true, length: 12, length2: 8},
                data: channelRows.slice(0, 8),
            },
        ],
    };

    const productOption = moneyBarOption(productRows, currency, t("reporting_lab.series.revenue", {defaultValue: "Obrat"}), chartTextStyle);
    const paymentOption = moneyBarOption(paymentRows, null, t("orders.amount", {defaultValue: "Počet"}), chartTextStyle);
    const shippingOption = moneyBarOption(shippingRows, null, t("orders.amount", {defaultValue: "Počet"}), chartTextStyle);

    return (
        <WideSecureLayout retentionExpired={retentionExpired}
            title={t("reporting_lab.pages.daily", {defaultValue: "Denní výkon"})}
            actions={<OrderDashboardFilters hideFilters={true} showMarketingFilters={false} showModeSwitch={false}/>}
            alerts={isEmptySelectedDay
                ? [dailyEmptyStateAlert(t("reporting_lab.daily.empty_day", {defaultValue: "Pro tento den zatím nejsou k dispozici žádná data."}))]
                : [snapshotCompletenessAlert(currentSnapshots, t)]}
         intro={<ReportingPageDescription
                        className="col-span-12"
                        title={t("reporting_lab.descriptions.daily.title", {defaultValue: "K čemu slouží denní výkon"})}
                        actions={<DayModeToggle value={dayMode} onChange={setDayMode}/>}
                    >
                        {t("reporting_lab.descriptions.daily.text", {defaultValue: "Rychlý provozní pohled na dnešek nebo včerejšek: objednávky, obrat, marketing, top produkty, platby, dopravu, země, domény a případně střediska. KPI průměrné hodnoty níže je samostatný objednávkový pohled včetně nedokončených objednávek."})}
                    </ReportingPageDescription>}>
            <div className={`grid grid-cols-12 gap-4 ${isEmptySelectedDay ? "opacity-60 grayscale" : ""}`}>
                    {!isEmptySelectedDay && <CentreSpendAttributionNotice attribution={currentData?.centreSpendAttribution}/>}
                    {showAvailabilityNotice && <div role="status" className="app-surface col-span-12 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                        <span className="font-medium">{currentMetrics.availability === "incomplete"
                            ? t("reporting_lab.daily.snapshot_incomplete_short", {defaultValue: "Neúplná denní data"})
                            : t("reporting_lab.daily.snapshot_missing_short", {defaultValue: "Denní data nejsou dostupná"})}</span>
                        <ReportingInfoTooltip ariaLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}>
                            {currentMetrics.availability === "incomplete"
                                ? t("reporting_lab.daily.snapshot_incomplete_help", {defaultValue: "Denní data nejsou úplně připravená. Souhrny a srovnání za tento den proto nejsou dostupné."})
                                : t("reporting_lab.daily.snapshot_missing_help", {defaultValue: "Objednávková data pro tento den nejsou dostupná. Chybějící hodnoty proto nezobrazujeme jako nuly."})}
                        </ReportingInfoTooltip>
                    </div>}
                    <div className="col-span-12 md:col-span-3">
                        <KpiCard loading={currentLoading.core}
                            label={t("reporting_lab.daily.orders", {defaultValue: "Objednávky"})}
                            value={isEmptySelectedDay ? "—" : valueWithComparison(ordersComparison.current === null ? "-" : renderNumber(ordersComparison.current), ordersComparison.current, ordersComparison.previousDay, t)}
                            meta={isEmptySelectedDay || ordersBasis === null ? null : <OrderStatusSummary
                                received={currentData?.orders?.ordersReceived ?? 0}
                                completed={currentData?.orders?.ordersCompleted ?? 0}
                                cancelled={currentData?.orders?.ordersCancelled ?? 0}
                                renderNumber={renderNumber}
                                t={t}
                            />}
                            helper={isEmptySelectedDay ? null : comparisonWeekMeta(ordersComparison.current, ordersComparison.previousWeek, renderNumber, t)}
                        />
                    </div>
                    <div className="col-span-12 md:col-span-3">
                        <KpiCard loading={currentLoading.core} label={t("reporting_lab.daily.turnover", {defaultValue: "Obrat"})} value={isEmptySelectedDay ? "—" : valueWithComparison(turnover === null ? "-" : renderCurrencyValue({amount: turnover, currency}, true), turnover, previousDayValues.turnover, t)} meta={isEmptySelectedDay ? null : t("reporting_lab.daily.by_centres", {defaultValue: "Celkem bez storen"})} helper={isEmptySelectedDay ? null : comparisonWeekMeta(turnover, previousWeekValues.turnover, (value) => renderCurrencyValue({amount: value, currency}, true), t)}/>
                    </div>
                    <div className="col-span-12 md:col-span-3">
                        <KpiCard loading={currentLoading.core} label="PNO" value={isEmptySelectedDay ? "—" : valueWithComparison(pno === null ? "-" : `${formatChartNumber(pno)} %`, pno, previousDayValues.pno, t, true, true)} meta={isEmptySelectedDay ? null : t("reporting_lab.daily.marketing_cost", {defaultValue: "Marketing"}) + ` ${marketingSpend === null ? "-" : renderCurrencyValue({amount: marketingSpend, currency}, true)}`} helper={isEmptySelectedDay ? null : comparisonWeekMeta(pno, previousWeekValues.pno, (value) => `${formatChartNumber(value, {maximumFractionDigits: 1})} %`, t, true, true)}/>
                    </div>
                    <div className="col-span-12 md:col-span-3">
                        <KpiCard loading={currentLoading.core} label={t("reporting_lab.daily.average_order_value", {defaultValue: "Průměrná hodnota objednávky"})} value={isEmptySelectedDay ? "—" : valueWithComparison(averageOrderValue === null ? "-" : renderCurrencyValue({amount: averageOrderValue, currency}, true), averageOrderValue, previousDayValues.averageOrderValue, t)} meta={isEmptySelectedDay ? null : t("reporting_lab.daily.aov_scope_short", {defaultValue: "Bez storen"})} info={t("reporting_lab.daily.aov_meta", {defaultValue: "Průměrná hodnota ze všech způsobilých nestornovaných objednávek ve zvoleném dni a rozsahu."})} infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})} helper={isEmptySelectedDay ? null : comparisonWeekMeta(averageOrderValue, previousWeekValues.averageOrderValue, (value) => renderCurrencyValue({amount: value, currency}, true), t)}/>
                    </div>

                    <Panel loading={currentLoading.core} title={<span className="inline-flex items-center gap-2">{t("reporting_lab.daily.channel_share", {defaultValue: "Podíl zobrazených kanálů"})}<ReportingInfoTooltip>{t("reporting_lab.daily.channel_share_info", {defaultValue: "Podíl se počítá z přiřazeného obratu zobrazených kanálů (nejvýše osmi), ne z celkového obratu obchodu."})}</ReportingInfoTooltip></span>} className="col-span-12 xl:col-span-5">
                        <EChart option={channelOption} height={380}/>
                    </Panel>
                    <Panel loading={currentLoading.products} title={t("reporting_lab.daily.top_products", {defaultValue: "Top produkty dne"})} className="col-span-12 xl:col-span-7">
                        {!isEmptySelectedDay && <ReportingSnapshotStatus snapshots={productPublication}/>}
                        {isEmptySelectedDay
                            ? <EChart option={productOption} height={380}/>
                            : snapshotHasPublishedDays(productPublication)
                            ? <EChart option={productOption} height={380}/>
                            : <div className="text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.snapshot.no_published_days_short", {defaultValue: "Bez publikovaných dnů"})}</div>}
                    </Panel>

                    <Panel loading={currentLoading.payments} title={t("reporting_lab.daily.payments", {defaultValue: "Platby"})} className="col-span-12 xl:col-span-4">
                        <EChart option={paymentOption} height={340}/>
                    </Panel>
                    <Panel loading={currentLoading.shippings} title={t("reporting_lab.daily.shipping", {defaultValue: "Doprava"})} className="col-span-12 xl:col-span-4">
                        <EChart option={shippingOption} height={340}/>
                    </Panel>
                    <Panel loading={currentLoading.countries} title={t("reporting_lab.daily.countries", {defaultValue: "Země"})} className="col-span-12 xl:col-span-4">
                        <RankingTable rows={countryRows} label={t("reporting_lab.daily.country", {defaultValue: "Země"})} amountLabel={t("orders.amount", {defaultValue: "Počet"})} renderNumber={renderNumber}/>
                    </Panel>

                    <Panel loading={currentLoading.core} title={t("reporting_lab.daily.domains", {defaultValue: "Domény"})} className="col-span-12 xl:col-span-6">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                                <thead>
                                <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                    <th className="py-2 pr-4 font-semibold">{t("reporting_lab.daily.domain", {defaultValue: "Doména"})}</th>
                                    <th className="py-2 text-right font-semibold">{t("orders.turnover", {defaultValue: "Obrat"})}</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {domainRows.map((domain) => (
                                    <tr key={domain.name}>
                                        <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{domain.name}</td>
                                        <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue({amount: domain.turnoverAmount, currency}, true)}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    <Panel loading={currentLoading.coupons} title={t("reporting_lab.daily.coupons", {defaultValue: "Kupony dne"})} className="col-span-12 xl:col-span-6">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                                <thead>
                                <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                    <th className="py-2 pr-4 font-semibold">{t("reporting_lab.discounts.coupon", {defaultValue: "Kupon"})}</th>
                                    <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.discounts.usage", {defaultValue: "Použití"})}</th>
                                    <th className="py-2 text-right font-semibold">{t("reporting_lab.table.revenue", {defaultValue: "Obrat"})}</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {couponRows.slice(0, 10).map((coupon) => (
                                    <tr key={coupon.name}>
                                        <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{coupon.name}</td>
                                        <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(coupon.amount)}</td>
                                        <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue({amount: coupon.turnover, currency}, true)}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    {centres.length > 1 && <Panel loading={currentLoading.core} title={t("reporting_lab.daily.centres", {defaultValue: "Střediska"})} className="col-span-12">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                                <thead>
                                <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                    <th className="py-2 pr-4 font-semibold">{t("orders.centre", {defaultValue: "Středisko"})}</th>
                                    <th className="py-2 pr-4 text-right font-semibold">{t("orders.amount", {defaultValue: "Počet"})}</th>
                                    <th className="py-2 pr-4 text-right font-semibold">{t("orders.turnover", {defaultValue: "Obrat"})}</th>
                                    <th className="py-2 text-right font-semibold">{t("orders.average-order-price", {defaultValue: "Prům. hod. obj."})}</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {centres.map((centre) => (
                                    <tr key={centre.centreName}>
                                        <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{centre.centreName}</td>
                                        <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(centre.orderAmount)}</td>
                                        <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(centre.turnover, true)}</td>
                                        <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(reportedCentreAverageOrderValue(centre), true)}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>}
            </div>
        </WideSecureLayout>
    );
}

function RankingTable({rows, label, amountLabel, renderNumber}) {
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                <thead>
                <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                    <th className="py-2 pr-4 font-semibold">{label}</th>
                    <th className="py-2 text-right font-semibold">{amountLabel}</th>
                </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {rows.map((row) => (
                    <tr key={row.name}>
                        <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{row.name}</td>
                        <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.value)}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
}
